'use client';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Block } from './types';
import { BLOCK_GROUPS, BLOCK_LABELS } from './types';

const BLOCK_ICONS: Record<Block['type'], string> = {
  paragraph: '¶',
  lede: 'L¶',
  h2: 'H2',
  h3: 'H3',
  h4: 'H4',
  blockquote: '"',
  pullquote: '❝',
  ul: '•',
  ol: '1.',
  code: '</>',
  hr: '—',
  BlogImage: '🖼',
  ImageCarousel: '◫',
  ImageGrid: '⊞',
  BlogVideo: '▶',
  Callout: '💡',
  ReadingCallout: '📌',
  BlogTable: '⊞',
  Columns: '⫴',
  AuthorCard: '👤',
  InlineCTA: '★',
  TLDR: 'TL',
  StatsGrid: '▦',
  rawHtml: 'HTML',
};

interface Props {
  anchorEl: HTMLElement | null;
  onPick: (type: Block['type']) => void;
  onClose: () => void;
  /** Optional override for which block groups / types to show */
  groups?: typeof BLOCK_GROUPS;
}

export function BlockPickerPopup({ anchorEl, onPick, onClose, groups }: Props) {
  const [search, setSearch] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Calculate position — clamp so popup stays within viewport and below topbar
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false });
  useEffect(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const TOPBAR_H = 48; // be-topbar height
    const POPUP_H  = 420; // max expected popup height
    const left = Math.max(8, Math.min(rect.left, vw - 296));
    // Open downward if space below, otherwise open upward
    const spaceBelow = vh - rect.bottom - 8;
    const openUp = spaceBelow < POPUP_H && rect.top - TOPBAR_H > POPUP_H / 2;
    const top = openUp
      ? Math.max(TOPBAR_H + 4, rect.top - 8) // anchor to top edge of button, going up
      : Math.max(TOPBAR_H + 4, rect.bottom + 8);
    setPos({ top, left, openUp });
  }, [anchorEl]);

  useEffect(() => { searchRef.current?.focus(); }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node) && e.target !== anchorEl) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorEl]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const q = search.toLowerCase();
  const activeGroups = groups ?? BLOCK_GROUPS;
  const filteredGroups = activeGroups.map(g => ({
    ...g,
    types: q
      ? g.types.filter(t => BLOCK_LABELS[t].toLowerCase().includes(q))
      : g.types,
  })).filter(g => g.types.length > 0);

  const allTypes = filteredGroups.flatMap(g => g.types);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, allTypes.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); if (allTypes[activeIdx]) onPick(allTypes[activeIdx]); }
  };

  return createPortal(
    <div
      ref={popupRef}
      className="be-picker"
      style={{
        top: pos.openUp ? 'auto' : pos.top,
        bottom: pos.openUp ? `calc(100vh - ${pos.top}px)` : 'auto',
        left: pos.left,
        zIndex: 9999,
      }}
      onKeyDown={handleKey}
    >
      <div className="be-picker__search">
        <input
          ref={searchRef}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setActiveIdx(0); }}
          placeholder="Search blocks…"
        />
      </div>
      <div className="be-picker__scroll">
        {filteredGroups.map(group => (
          <div key={group.label}>
            <div className="be-picker__group-label">{group.label}</div>
            {group.types.map((type) => {
              const globalIdx = allTypes.indexOf(type);
              return (
                <button
                  key={type}
                  className={`be-picker__item${globalIdx === activeIdx ? ' is-active' : ''}`}
                  onMouseEnter={() => setActiveIdx(globalIdx)}
                  onClick={() => onPick(type)}
                >
                  <span className="be-picker__item__icon">{BLOCK_ICONS[type]}</span>
                  {BLOCK_LABELS[type]}
                </button>
              );
            })}
          </div>
        ))}
        {allTypes.length === 0 && (
          <div style={{ padding: '16px', textAlign: 'center', fontSize: 13, color: 'var(--seasons-color-text-placeholder)' }}>
            No blocks match "{search}"
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
